/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { inject, Injectable } from '@angular/core';
import { FabUser } from '@fabric/components/common';
import { OAuthService } from 'angular-oauth2-oidc';

import { EnvironmentService } from '../environment/environment.service';

@Injectable()
export class AuthenticationService {
  /**
   * Observable which will return the user's information from the token
   * @returns user object created from identity claims
   */
  get userInfo(): FabUser {
    const claims = this.oAuthService.getIdentityClaims();
    return this.constructUserFromIdentityClaims(claims);
  }

  private readonly environmentService: EnvironmentService = inject(EnvironmentService);
  private readonly oAuthService: OAuthService = inject(OAuthService);

  async initialize(): Promise<boolean> {
    this.oAuthService.configure(this.environmentService.environment.auth ?? {});
    return this.oAuthService.loadDiscoveryDocumentAndLogin();
  }

  /**
   * Will set up up silent refreshing for when the token is about to expire.
   * When the user is logged out via this.logOut method, the silent refreshing will pause and not refresh the tokens
   * until the user is logged back in via receiving a new token.
   */
  enableSilentRenew(): void {
    this.oAuthService.setupAutomaticSilentRefresh();
  }

  /**
   * Removes all tokens and logs the user out. If a logout url is configured,
   * the user is redirected to it.
   */
  logout(): void {
    this.oAuthService.logOut();
  }

  /**
   * Creates a FabUser from the claims object passed in.
   * @param claims - Object consisting of the user's properties
   * @returns user object created from identity claims
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private constructUserFromIdentityClaims(claims: Record<string, any> = {}): FabUser {
    return {
      gmin: claims.gmuniqueidentifier ?? '',
      firstName: claims.givenName ?? '',
      lastName: claims.sn ?? '',
      displayName: claims.name ?? '',
      email: claims.email ?? '',
      roles: claims.roles ?? [],
    };
  }
}
